/**
 * A ÁREA DE CONFIGURAÇÃO (20/10, "reimaginar tudo"): as cinco telas do menu
 * Configuração — Configuração, Acervo, Fila, Vocabulário e Extração — numa
 * área só, com a navegação entre elas sempre à vista. No desktop ela é uma
 * coluna à esquerda; no celular, uma linha de abas que rola. Antes, ir de uma
 * a outra era voltar ao menu do alto, e cada uma era uma página solta.
 *
 * A ORDEM É A DO MENU (`CONFIGURACAO` em `Moldura.tsx`), com os MESMOS nomes:
 * duas listas para as mesmas telas divergem. Os nomes são os de quem decide, e
 * não os do sistema (20/10, "o meu chefe não consegue entender nada disso"):
 * "Fila" virou "Funil de seleção", "Acervo" (que se confundia com o acervo de
 * atestados) virou "Base de licitações", e assim por diante. As rotas não
 * mudaram.
 */
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import css from "./AreaConfig.module.css";

const SECOES: { para: string; rotulo: string; resumo: string; icone: ReactNode }[] = [
  {
    para: "/configuracao",
    rotulo: "Regras e ajustes",
    resumo: "Como o sistema decide e trabalha",
    icone: (
      <>
        <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0" />
        <circle cx="16" cy="6" r="2" />
        <circle cx="10" cy="12" r="2" />
        <circle cx="18" cy="18" r="2" />
      </>
    ),
  },
  {
    para: "/acervo",
    rotulo: "Base de licitações",
    resumo: "Tudo o que já foi encontrado",
    icone: (
      <>
        <ellipse cx="12" cy="6" rx="7" ry="2.6" />
        <path d="M5 6v6c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6V6M5 12v6c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6v-6" />
      </>
    ),
  },
  {
    para: "/fila",
    rotulo: "Funil de seleção",
    resumo: "O que passa por cada filtro",
    icone: <path d="M4 5h16l-6 7.5V19l-4-2v-4.5L4 5Z" />,
  },
  {
    para: "/vocabulario",
    rotulo: "Palavras de busca",
    resumo: "O que procuramos nos portais",
    icone: (
      <>
        <path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3V4Z" />
        <path d="M5 17a3 3 0 0 1 3-3h11M9 8h6" />
      </>
    ),
  },
  {
    para: "/extracao",
    rotulo: "Buscas nos portais",
    resumo: "Rodar e acompanhar as buscas",
    icone: (
      <>
        <path d="M12 3v12M7.5 10.5 12 15l4.5-4.5" />
        <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
      </>
    ),
  },
];

export function AreaConfig({
  titulo,
  descricao,
  acoes,
  children,
}: {
  titulo: string;
  descricao?: ReactNode;
  /** O que fica à direita do título (uma busca, um botão). */
  acoes?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={css.area}>
      <nav className={css.lateral} aria-label="Administração">
        <p className={css.lateralTitulo}>Administração</p>
        <ul>
          {SECOES.map((s) => (
            <li key={s.para}>
              <NavLink to={s.para} className={({ isActive }) => (isActive ? css.ativa : "")}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                     strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {s.icone}
                </svg>
                <span className={css.rotulo}>
                  {s.rotulo}
                  <small>{s.resumo}</small>
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className={css.conteudo}>
        <header className={css.cabeca}>
          <div className={css.titulos}>
            <h1>{titulo}</h1>
            {descricao && <p>{descricao}</p>}
          </div>
          {acoes && <div className={css.acoes}>{acoes}</div>}
        </header>
        {children}
      </div>
    </div>
  );
}
